import React, { useRef, useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Input, Modal, ToolTip, BaseButton, useT } from '@repo/common';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const KeyAchievementForm = ({ experienceId, achievements = [], onChange }) => {
  const t = useT();
  const inputRefs = useRef({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);

  const addAchievement = () => {
    const newItem = {
      id: uuidv4(),
      title: '',
      description: '',
    };

    const updated = [...achievements, newItem];
    onChange(updated);

    requestAnimationFrame(() => {
      inputRefs.current[`title-${newItem.id}`]?.focus();
    });
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    onChange(achievements.filter((item) => item.id !== targetId));
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleChange = (id, field, value) => {
    onChange(
      achievements.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(achievements);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    onChange(items);
  };

  const didFocus = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (didFocus.current) return;
    if (achievements.length === 0) return;

    const firstId = achievements[0].id;
    setTimeout(() => {
      inputRefs.current[`title-${firstId}`]?.focus();
      didFocus.current = true;
    }, 0);
  }, [achievements]);

  return (
    <div className='flex flex-col w-full gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={`key-achievements-${experienceId}`}
          direction='vertical'
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className='flex flex-col gap-4'
            >
              {achievements.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        ...(snapshot.isDragging && {
                          pointerEvents: 'none',
                        }),
                      }}
                      className='flex items-start'
                    >
                      {/* Drag Handle */}
                      <div
                        {...provided.dragHandleProps}
                        className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                        onMouseEnter={() => setHoverDragAndDropId(item.id)}
                        onMouseLeave={() => setHoverDragAndDropId(null)}
                      >
                        <DragAndDropIcon width={33} height={33} />
                        {hoverDragAndDropId === item.id && (
                          <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                            <ToolTip
                              label='Click and drag to move'
                              labelKr='클릭하고 드래그하여 이동'
                              position='bottom'
                              align='center'
                              state='accent'
                            />
                          </div>
                        )}
                      </div>

                      {/* Form */}
                      <div className='flex-1 border rounded-[8px] p-6 pb-9 bg-green-light01 border-green-light03'>
                        <div className='flex flex-col gap-4'>
                          {/* Achievement Title */}
                          <div className='flex flex-col gap-2'>
                            <span className='text-text-primary'>
                              {t('Achievement title', '성과 제목')}
                              <span className='text-functional-accent03 ml-1'>
                                *
                              </span>
                            </span>
                            <span className='typo-b12 text-text-placeholder'>
                              {t(
                                'Quantify your achievements where possible (Outcome-focused, measurable result)',
                                '가능하다면 성과를 수치로 표현해 주세요 (결과 중심의 측정 가능한 성과)',
                              )}
                            </span>
                            <Input
                              ref={(el) =>
                                (inputRefs.current[`title-${item.id}`] = el)
                              }
                              label=''
                              placeholder={t(
                                'e.g. Increased user engagement by 12%',
                                '예: 사용자 참여도 12% 증가',
                              )}
                              value={item.title}
                              onChange={(e) =>
                                handleChange(item.id, 'title', e.target.value)
                              }
                              hideCount
                            />
                          </div>

                          {/* Description */}
                          <div className='flex flex-col gap-2'>
                            <span className='text-text-primary'>
                              {t('Description (optional)', '설명 (선택 사항)')}
                            </span>
                            <span className='typo-b12 text-text-placeholder'>
                              {t(
                                'Explain how you achieved this result (What you did + how you contributed: tools, actions, collaboration)',
                                '이 결과를 어떻게 달성했는지 설명해 주세요 (수행한 일과 기여한 방식: 도구, 행동, 협업)',
                              )}
                            </span>
                            <Input
                              as='textarea'
                              ref={(el) =>
                                (inputRefs.current[`description-${item.id}`] =
                                  el)
                              }
                              label=''
                              placeholder={t(
                                'e.g. Designed visual UI components for the product detail page, focusing on visual hierarchy, layout balance, and clarity.',
                                '예: 제품 상세 페이지의 시각적 UI 컴포넌트를 디자인하고, 시각적 계층 구조와 레이아웃 균형 및 명확성에 집중했습니다.',
                              )}
                              value={item.description}
                              onChange={(e) =>
                                handleChange(
                                  item.id,
                                  'description',
                                  e.target.value,
                                )
                              }
                              maxLength={500}
                              textareaRows={4}
                              textareaMinHeight='122px'
                              className='max-h-[122px]'
                            />
                          </div>
                        </div>
                      </div>

                      {/* Delete */}
                      <div className='flex-shrink-0 pt-1 relative'>
                        <button
                          className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                          onClick={() => openConfirmModal(item.id)}
                          onMouseEnter={() => setHoverDeleteId(item.id)}
                          onMouseLeave={() => setHoverDeleteId(null)}
                        >
                          <DeleteIcon width={24} height={24} />
                        </button>
                        {hoverDeleteId === item.id && (
                          <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                            <ToolTip
                              label='Delete'
                              labelKr='삭제'
                              position='bottom'
                              align='center'
                              state='accent'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add */}
      <div className='px-[33px]'>
        <BaseButton
          type='secondary'
          icon={<PlusIcon />}
          width='228px'
          onClick={addAchievement}
          textWrapperClass='flex-none'
        >
          {t('Add one more Achievement', '성과 추가')}
        </BaseButton>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <Modal
          isOpen
          title={t('Delete this key achievement?', '이 성과를 삭제할까요?')}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 취소할 수 없습니다.',
          )}
          leftButtonLabel={t('Cancel', '취소')}
          rightButtonLabel={t('Delete', '삭제')}
          onClickLeftBtn={() => setShowDeleteModal(false)}
          onClickRightBtn={handleConfirmDelete}
          type='warning'
        />
      )}
    </div>
  );
};

export default KeyAchievementForm;
